import { ApiResponse, PaginationResponse } from '../../../../constants/types';
import {
  AssignmentClassItem,
  AssignmentItem,
  AssignmentStatusByClassItem,
  AssignmentStatusByStudentItem,
  AssignmentStatusDetailByClassItem,
  AssignmentStatusDetailByStudentItem,
  AssignmentStudentItem,
  ClassGroupSummaryItem,
  AssignmentOrderByType,
  AssignmentRecordingItem,
  AssignmentRecordingDownloadUrl,
  VoucherBalanceItem,
} from '../../../../constants/types/lms/assignment/type';
import {
  AssignmentStatusType,
  AssignmentType,
} from '../../../../constants/types/lms/type';
import instance from '../../../axios/utils.ts/instance';

interface AssignmentBookListParams {
  classId: string;
  type: string;
}

/**
 * LMS > 과제 관리 > 과제 내주기 : 북 목록 조회
 * @param classId
 * @param type
 * @returns
 */
export const getAssignmentBookList = async (
  params: AssignmentBookListParams,
) => {
  const { classId, ...rest } = params;

  const response = await instance.get<ApiResponse<string[]>>(
    `/v1/lms/${classId}/todo/book-filter`,
    {
      params: rest,
    },
  );

  return response.data.data;
};

interface AssignmentLessonListParams {
  classId: string;
  type: string;
  book: string;
}

/**
 * LMS > 과제 관리 > 과제 내주기 : 레슨 목록 조회
 * @param classId
 * @param type
 * @param book
 * @returns
 */
export const getAssignmentLessonList = async (
  params: AssignmentLessonListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<ApiResponse<string[]>>(
    `/v1/lms/${classId}/todo/lesson-filter`,
    {
      params: rest,
    },
  );

  return result.data.data;
};

interface AssignmentListParams {
  classId: string;
  type?: string;
  book?: string;
  lesson?: string;
}

/**
 * LMS > 과제 관리 > 과제 내주기 : 과제 목록 조회
 * @param classId
 * @param type
 * @param book
 * @param lesson
 * @returns
 */
export const getAssignmentList = async (params: AssignmentListParams) => {
  const { classId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentItem[]>>
  >(`/v1/lms/${classId}/todo`, { params: { page: 1, pageSize: 50, ...rest } });

  return result.data.data.result;
};

interface AssignmentClassListParams {
  classId: string;
}

/**
 * LMS > 과제 관리 > 과제 내주기 : 반 목록 조회
 * @param classId
 * @returns
 */
export const getAssignmentClassList = async (
  params: AssignmentClassListParams,
) => {
  const { classId } = params;

  const result = await instance.get<ApiResponse<AssignmentClassItem[]>>(
    `/v1/lms/${classId}/todo/class-group-filter`,
  );

  return result.data.data;
};

interface AssignmentStudentListParams {
  classId: string;
  classGroupId?: number | number[];
}

/**
 * LMS > 과제 관리 > 과제 내주기 : 학생 목록 조회
 * @param classId
 * @param classGroupId
 * @returns
 */
export const getAssignmentStudentList = async (
  params: AssignmentStudentListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<ApiResponse<AssignmentStudentItem[]>>(
    `/v1/lms/${classId}/todo/student`,
    {
      params: rest,
    },
  );

  return result.data.data;
};

interface PostAssignmentsToStudentsParams {
  classId: string;
  toDoIds: number[];
  studentIds: number[];
  toDate: string;
  confirmDuplicates?: boolean;
}

/**
 * LMS > 과제 관리 > 과제 내주기 : 과제 배정
 * @param classId
 * @param toDoIds
 * @param studentIds
 * @param toDate
 * @param confirmDuplicates
 * @returns
 */
export const postAssignmentsToStudents = async (
  params: PostAssignmentsToStudentsParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.post(`/v1/lms/${classId}/todo/assign`, rest);

  return result.data;
};

/**
 * LMS > 과제 관리 > 과제 내주기 : 이용권 잔여 수량 조회
 * @returns
 */
export const getVoucherBalanceList = async () => {
  const result = await instance.get<
    ApiResponse<{ items: VoucherBalanceItem[] }>
  >(`/v1/lms/vouchers/balance`);

  return result.data.data;
};

/**
 * -----------------------------------------------------------------
 */

interface ClassGroupSummaryListParams {
  classId: string;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 반 목록 조회
 * @param classId
 * @returns
 */
export const getClassGroupSummaryList = async (
  params: ClassGroupSummaryListParams,
) => {
  const { classId } = params;

  const result = await instance.get<
    ApiResponse<{ groups: ClassGroupSummaryItem[] }>
  >(`/v1/lms/${classId}/todo/class-group-summary`);

  return result.data.data;
};

interface AssignmentStatusListParams {
  classId: string;
  classGroupId?: number;
  unassignedGroup?: boolean;
  page?: number;
  pageSize?: number;
  toDoStatus?: AssignmentStatusType;
  toDoType?: AssignmentType;
  book?: string;
  lesson?: string;
  title?: string;
  uid?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  dateType?: 'ASSIGNED' | 'COMPLETED';
  orderBy?: AssignmentOrderByType;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 반별 과제 현황 목록 조회
 * @param classId
 * @param classGroupId
 * @param unassignedGroup
 * @param page
 * @param pageSize
 * @param toDoStatus
 * @param toDoType
 * @param book
 * @param lesson
 * @param title
 * @param uid
 * @param name
 * @param fromDate
 * @param toDate
 * @param dateType
 * @param orderBy
 * @returns
 */
export const getAssignmentStatusListByClass = async (
  params: AssignmentStatusListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentStatusByClassItem[]>>
  >(`/v1/lms/${classId}/todo/state`, {
    params: { category: 'BY_TODO', ...rest },
  });

  return result.data.data;
};

/**
 * LMS > 과제 관리 > 과제 현황 : 학생별 과제 현황 목록 조회
 * @param classId
 * @param toDoStatus
 * @param toDoType
 * @param book
 * @param lesson
 * @param title
 * @param uid
 * @param name
 * @param orderBy
 * @returns
 */
export const getAssignmentStatusListByStudent = async (
  params: AssignmentStatusListParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentStatusByStudentItem[]>>
  >(`/v1/lms/${classId}/todo/state`, {
    params: { category: 'BY_ID', ...rest },
  });

  return result.data.data;
};

interface AssignmentStatusDetailListByClassParams {
  classId: string;
  detailId: string;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 반별 과제 현황 상세 조회
 * @param classId
 * @param detailId
 * @returns
 */
export const getAssignmentStatusDetailListByClass = async (
  params: AssignmentStatusDetailListByClassParams,
) => {
  const { classId, detailId } = params;

  const result = await instance.get<
    ApiResponse<AssignmentStatusDetailByClassItem[]>
  >(`/v1/lms/${classId}/todo/state/${detailId}`, {
    params: { category: 'BY_TODO' },
  });

  return result.data.data;
};

interface AssignmentStatusDetailListByStudentParams {
  classId: string;
  detailId: number;
  page: number;
  pageSize: number;
  toDoStatus?: AssignmentStatusType;
  toDoType?: AssignmentType;
  book?: string;
  lesson?: string;
  title?: string;
  uid?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  dateType?: 'ASSIGNED' | 'COMPLETED';
  orderBy?: AssignmentOrderByType;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 학생별 과제 현황 상세 조회
 * @param classId
 * @param detailId
 * @param page
 * @param pageSize
 * @param toDoStatus
 * @param toDoType
 * @param book
 * @param lesson
 * @param title
 * @param uid
 * @param name
 * @param fromDate
 * @param toDate
 * @param dateType
 * @param orderBy
 * @returns
 */
export const getAssignmentStatusDetailListByStudent = async (
  params: AssignmentStatusDetailListByStudentParams,
) => {
  const { classId, detailId, ...rest } = params;

  const result = await instance.get<
    ApiResponse<PaginationResponse<AssignmentStatusDetailByStudentItem[]>>
  >(`/v1/lms/${classId}/todo/state/${detailId}`, {
    params: { category: 'BY_ID', ...rest },
  });

  return result.data.data;
};

interface DeleteAssignmentParams {
  classId: string;
  txId: string;
  todoStatus: AssignmentStatusType;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 과제 삭제
 * @param classId
 * @param txId
 * @param todoStatus
 * @returns
 */
export const deleteAssignment = async (params: DeleteAssignmentParams) => {
  const { classId, txId, todoStatus } = params;

  const result = await instance.delete(
    `/v1/lms/${classId}/todo/assign/${txId}?todoStatus=${todoStatus}`,
  );

  return result.data;
};

interface DeleteAssignmentByStudentParams {
  classId: string;
  todoId: number;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 학생 단위 과제 삭제
 * @param classId
 * @param todoId
 * @returns
 */
export const deleteAssignmentByStudent = async (
  params: DeleteAssignmentByStudentParams,
) => {
  const { classId, todoId } = params;

  const result = await instance.delete(
    `/v1/lms/${classId}/todo/assigned/${todoId}`,
  );

  return result.data;
};

interface DeleteAssignmentBulkParams {
  classId: string;
  assignedIds: number[];
}

/**
 * LMS > 과제 관리 > 과제 현황 : 과제 일괄 삭제
 * @param classId
 * @param assignedIds
 * @returns
 */
export const deleteAssignmentBulk = async (
  params: DeleteAssignmentBulkParams,
) => {
  const { classId, ...rest } = params;

  const result = await instance.post(
    `/v1/lms/${classId}/todo/assigned/bulk-delete`,
    rest,
  );

  return result.data;
};

/**
 * -----------------------------------------------------------------
 */

interface AssignmentRecordingListParams {
  studentId: number;
  todoId: number;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 학생별 녹음 목록 조회
 * @param studentId
 * @param todoId
 * @returns
 */
export const getAssignmentRecordingList = async (
  params: AssignmentRecordingListParams,
) => {
  const result = await instance.get<
    ApiResponse<{ items: AssignmentRecordingItem[] }>
  >(`/v1/lms/recordings`, {
    params,
  });

  return result.data.data;
};

interface EvaluateAssignmentRecording {
  recordingId: number;
  status: 'approved' | 'rejected';
  comment?: string;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 녹음 평가
 * @param recordingId
 * @param status
 * @param comment
 * @returns
 */
export const evaluateAssignmentRecording = async (
  params: EvaluateAssignmentRecording,
) => {
  const { recordingId, ...rest } = params;

  const result = await instance.patch(
    `/v1/lms/recordings/${recordingId}/evaluate`,
    rest,
  );

  return result.data;
};

interface RequestRetryAssignmentRecording {
  recordingId: number;
  comment?: string;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 재녹음 요청
 * @param recordingId
 * @param comment
 * @returns
 */
export const requestRetryAssignmentRecording = async (
  params: RequestRetryAssignmentRecording,
) => {
  const { recordingId, ...rest } = params;

  const result = await instance.post(
    `/v1/lms/recordings/${recordingId}/request-retry`,
    rest,
  );

  return result.data;
};

interface AssignmentRecordingDownloadUrlParams {
  recordingId: number;
}

/**
 * LMS > 과제 관리 > 과제 현황 : 녹음 파일 다운로드 조회
 * @param recordingId
 * @returns
 */
export const getAssignmentRecordingDownloadUrl = async (
  params: AssignmentRecordingDownloadUrlParams,
) => {
  const { recordingId } = params;

  const result = await instance.get<
    ApiResponse<AssignmentRecordingDownloadUrl>
  >(`/v1/lms/recordings/${recordingId}/download-url`);

  return result.data.data;
};
